/* eslint-disable */
export class Confetti {
  constructor() {
    this.particles = [];
    this.animationId = null;
    this.canvas = null;
    this.ctx = null;
    this.rainInterval = null; // Add this to track rain interval
    this.colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#a29bfe'];
  }

  // Initialize canvas if it doesn't exist
  initCanvas() {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.style.position = 'fixed';
      this.canvas.style.top = '0';
      this.canvas.style.left = '0';
      this.canvas.style.width = '100%';
      this.canvas.style.height = '100%';
      this.canvas.style.pointerEvents = 'none';
      this.canvas.style.zIndex = '9999';
      document.body.appendChild(this.canvas);

      this.ctx = this.canvas.getContext('2d');

      // Handle window resize
      window.addEventListener('resize', () => this.resizeCanvas());
    }

    // Always ensure canvas is properly sized
    this.resizeCanvas();
  }

  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  // Create a single confetti particle
  createParticle(x, y, velocityX, velocityY, options = {}) {
    return {
      x: x,
      y: y,
      velocityX: velocityX + (Math.random() - 0.5) * (options.spread || 4),
      velocityY: velocityY + (Math.random() - 0.5) * 2,
      color: this.colors[Math.floor(Math.random() * this.colors.length)],
      size: Math.random() * 6 + 2,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      gravity: options.gravity || 0.2,
      friction: options.friction || 0.895,
      life: 1.0,
      decay: Math.random() * 0.02 + 0.01
    };
  }

  // Main method to throw confetti
  throwConfetti(position = 'bottom', options = {}) {
    this.initCanvas();

    const count = options.count || 200;
    const spread = options.spread || 15;
    const velocity = options.velocity || 25;

    let startX, startY, baseVelocityX, baseVelocityY;

    // Determine starting position and initial velocity based on position parameter
    switch(position.toLowerCase()) {
      case 'bottom':
        startX = this.canvas.width / 2;
        startY = this.canvas.height;
        baseVelocityX = 0;
        baseVelocityY = -velocity;
        break;
      case 'top':
        startX = this.canvas.width / 2;
        startY = 0;
        baseVelocityX = 0;
        baseVelocityY = velocity;
        break;
      case 'left':
        startX = 0;
        startY = this.canvas.height / 2;
        baseVelocityX = velocity;
        baseVelocityY = 0;
        break;
      case 'right':
        startX = this.canvas.width;
        startY = this.canvas.height / 2;
        baseVelocityX = -velocity;
        baseVelocityY = 0;
        break;
      case 'center':
        startX = this.canvas.width / 2;
        startY = this.canvas.height / 2;
        baseVelocityX = 0;
        baseVelocityY = -velocity * 0.5;
        break;
      default:
        // If position is not recognized, try to parse as coordinates
        if (typeof position === 'object' && position.x !== undefined && position.y !== undefined) {
          startX = position.x;
          startY = position.y;
          baseVelocityX = 0;
          baseVelocityY = -velocity;
        } else {
          // Default to bottom
          startX = this.canvas.width / 2;
          startY = this.canvas.height;
          baseVelocityX = 0;
          baseVelocityY = -velocity;
        }
    }

    // Create particles
    for (let i = 0; i < count; i++) {
      this.particles.push(
          this.createParticle(startX, startY, baseVelocityX, baseVelocityY, { spread, ...options })
      );
    }

    // Start animation if not already running
    if (!this.animationId) {
      this.animate();
    }
  }

  // Rain confetti continuously for a duration
  rainConfetti(duration = 3000, options = {}) {
    this.initCanvas();

    const interval = options.interval || 150;
    const particlesPerDrop = options.particlesPerDrop || 5;
    const velocity = options.velocity || 8;
    const spread = options.spread || 2;

    let elapsed = 0;

    // Clear any existing rain interval
    if (this.rainInterval) {
      clearInterval(this.rainInterval);
    }

    // Force start animation immediately with a small delay to ensure canvas is ready
    setTimeout(() => {
      if (!this.animationId) {
        this.animate();
      }
    }, 10);

    // Start spawning particles immediately, then continue at intervals
    const spawnParticles = () => {
      // Ensure canvas is still valid and properly sized
      if (!this.canvas || !this.ctx || this.canvas.width === 0) {
        this.initCanvas();
      }

      // Create particles across the top of the screen
      for (let i = 0; i < particlesPerDrop; i++) {
        const x = Math.random() * this.canvas.width;
        const y = -20; // Start above screen

        this.particles.push(
            this.createParticle(x, y, 0, velocity, {
              spread: spread,
              gravity: options.gravity || 0.1,
              friction: options.friction || 0.99,
              ...options
            })
        );
      }
    };

    // Spawn initial particles immediately
    spawnParticles();

    // Continue spawning at intervals
    this.rainInterval = setInterval(() => {
      spawnParticles();

      elapsed += interval;
      if (elapsed >= duration) {
        clearInterval(this.rainInterval);
        this.rainInterval = null;
      }
    }, interval);
  }

  // Animation loop
  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];

      // Update particle physics
      particle.velocityY += particle.gravity;
      particle.velocityX *= particle.friction;
      particle.velocityY *= particle.friction;

      particle.x += particle.velocityX;
      particle.y += particle.velocityY;
      particle.rotation += particle.rotationSpeed;

      // Fade out over time
      particle.life -= particle.decay;

      // Remove particles that are off-screen or faded out
      if (particle.life <= 0 ||
          particle.y > this.canvas.height + 100 ||
          particle.x < -100 ||
          particle.x > this.canvas.width + 100) {
        this.particles.splice(i, 1);
        continue;
      }

      // Draw particle
      this.ctx.save();
      this.ctx.globalAlpha = particle.life;
      this.ctx.translate(particle.x, particle.y);
      this.ctx.rotate(particle.rotation * Math.PI / 180);

      this.ctx.fillStyle = particle.color;
      this.ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);

      this.ctx.restore();
    }

    // Continue animation if particles exist
    if (this.particles.length > 0) {
      this.animationId = requestAnimationFrame(() => this.animate());
    } else {
      this.animationId = null;
    }
  }

  // Clear all confetti
  clear() {
    this.particles = [];

    // Clear rain interval if running
    if (this.rainInterval) {
      clearInterval(this.rainInterval);
      this.rainInterval = null;
    }

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  // Remove canvas and clean up
  destroy() {
    this.clear();
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
      this.canvas = null;
      this.ctx = null;
    }
  }
}

// Create global instance for easy access
const confetti = new Confetti();

// Export convenience methods
export function throwConfetti(position = 'bottom', options = {}) {
  confetti.throwConfetti(position, options);
}

export function rainConfetti(duration = 3000, options = {}) {
  confetti.rainConfetti(duration, options);
}

export function clearConfetti() {
  confetti.clear();
}

export function destroyConfetti() {
  confetti.destroy();
}
